import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ScrollModal } from "../../../shared/scroll-modal";
import { ScrollModalApi } from "../../../shared/scroll-modal/view";
import useUserStore, { DiscordUser } from "../../../../store/UserState";
import useSongStore from "../store/SongState";
import { Spotip17anFunctions } from "../../../../utils/api";
import { DiscordBotApi, DiscordVoiceStatus } from "../../../../utils/discordBotApi";
import toast from "react-hot-toast";
import { DISCORD_CLIENT_ID } from "../../../../utils/constants";

export const DiscordVoiceModal = forwardRef<ScrollModalApi>((props, ref) => {
    const scrollModalRef = useRef<ScrollModalApi>(null);

    const [voiceStatus, setVoiceStatus] = useState<DiscordVoiceStatus | null>(null);
    const [loadingVoice, setLoadingVoice] = useState(false);
    const [botConnectionState, setBotConnectionState] = useState<'disponible' | 'conectado' | 'ocupado' | null>(null);
    const [botStateInfo, setBotStateInfo] = useState<any | null>(null);

    const discordUser = useUserStore(state => state.state.discordUser);
    const setDiscordUser = useUserStore(state => state.setDiscordUser);

    const { state: songState, setBotMode } = useSongStore(state => state);
    const { isBotMode } = songState;

    const location = useLocation();

    useImperativeHandle(ref, () => ({
        open: (e) => {
            scrollModalRef.current?.open(e);
            handleOpenDiscordVoiceModal();
        },
        close: () => {
            scrollModalRef.current?.close();
        }
    }));

    useEffect(() => {
        if (window.location.hash) {
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            const accessToken = hashParams.get('access_token');
            if (accessToken) {
                window.history.replaceState({}, document.title, window.location.pathname);

                const fetchDiscordUser = async () => {
                    try {
                        const res = await fetch('https://discord.com/api/users/@me', {
                            headers: {
                                Authorization: `Bearer ${accessToken}`,
                            },
                        });
                        if (!res.ok) {
                            throw new Error('No se pudo obtener el usuario de Discord');
                        }
                        const data = await res.json();
                        const userObj: DiscordUser = {
                            id: data.id,
                            username: data.username,
                            global_name: data.global_name || null,
                            avatar: data.avatar || null,
                        };

                        // Save in backend database
                        const saveDiscordProfile = Spotip17anFunctions.use('saveDiscordProfile');
                        await saveDiscordProfile.post({
                            discord_id: userObj.id,
                            discord_username: userObj.username,
                            discord_global_name: userObj.global_name,
                            discord_avatar: userObj.avatar
                        });

                        setDiscordUser(userObj);
                        toast.success(`Conectado a Discord como ${data.global_name || data.username}`);
                    } catch (err) {
                        console.error(err);
                        toast.error('Error al conectar con Discord');
                    }
                };

                fetchDiscordUser();
            }
        }
    }, [location]);

    const handleConnectDiscord = () => {
        if (!DISCORD_CLIENT_ID) {
            toast.error("El Client ID de Discord no está configurado en constants.ts");
            return;
        }
        const redirectUri = window.location.origin + window.location.pathname;
        const discordAuthUrl = `https://discord.com/api/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&redirect_uri=${encodeURIComponent(
            redirectUri
        )}&response_type=token&scope=identify`;
        window.location.href = discordAuthUrl;
    };

    const checkVoiceStatusForUser = async (userId: string) => {
        setLoadingVoice(true);
        setBotConnectionState(null);
        setBotStateInfo(null);
        try {
            const status = await DiscordBotApi.getUserVoiceStatus(userId);
            setVoiceStatus(status);

            if (status.success && status.guildId && status.voiceChannelId) {
                try {
                    const botState = await DiscordBotApi.getCurrentState(status.guildId);
                    if (botState && botState.state) {
                        setBotStateInfo(botState.state);
                        const isBotConnected = botState.state.connected;
                        const botChannelId = botState.state.voiceChannelId || botState.state.channelId;

                        if (isBotConnected) {
                            if (botChannelId === status.voiceChannelId) {
                                setBotConnectionState('conectado');
                            } else {
                                setBotConnectionState('ocupado');
                            }
                        } else {
                            setBotConnectionState('disponible');
                        }
                    } else {
                        setBotConnectionState('disponible');
                    }
                } catch (err) {
                    setBotConnectionState('disponible');
                }
            }
        } catch (error) {
            console.error("Error fetching voice status in header:", error);
            setVoiceStatus(null);
            setBotConnectionState(null);
        } finally {
            setLoadingVoice(false);
        }
    };

    const handleOpenDiscordVoiceModal = async () => {
        try {
            const getDiscordProfile = Spotip17anFunctions.use('getDiscordProfile');
            const res = await getDiscordProfile.post();

            if (res && res.discord_id) {
                const discordUserObj: DiscordUser = {
                    id: res.discord_id,
                    username: res.discord_username || "",
                    global_name: res.discord_global_name || null,
                    avatar: res.discord_avatar || null,
                };
                setDiscordUser(discordUserObj);
                await checkVoiceStatusForUser(res.discord_id);
            } else {
                setDiscordUser(null);
                setVoiceStatus(null);
            }
        } catch (err) {
            console.error("Error checking discord session in modal open:", err);
            if (discordUser?.id) {
                await checkVoiceStatusForUser(discordUser.id);
            }
        }
    };

    const handleInviteAndControl = async () => {
        if (!voiceStatus || !voiceStatus.guildId || !voiceStatus.voiceChannelId) return;
        try {
            await DiscordBotApi.play(voiceStatus.guildId, "", voiceStatus.voiceChannelId);
        } catch (err) {
            console.warn("Could not summon bot directly, continuing to enable bot mode:", err);
        }
        setBotMode(true, voiceStatus.guildId, voiceStatus.voiceChannelId);
        toast.success("Modo Bot activado. El bot se unirá al reproducir.");
    };

    const handleToggleBotMode = (active: boolean) => {
        if (active) {
            if (voiceStatus && voiceStatus.guildId && voiceStatus.voiceChannelId) {
                setBotMode(true, voiceStatus.guildId, voiceStatus.voiceChannelId);
                toast.success("Modo Bot activado. Controlando bot de Discord.");
            }
        } else {
            setBotMode(false);
            toast.success("Modo Bot desactivado. Reproducción local activada.");
        }
    };

    return (
        <ScrollModal
            ref={scrollModalRef}
            header={
                <h5 className="mb-0 d-flex align-items-center gap-2 text-white">
                    <i className="fa-brands fa-discord" style={{ color: '#5865F2' }}></i> Estado de Voz
                </h5>
            }
        >
            <div className="p-3 text-white discord-scroll-modal">
                {!discordUser ? (
                    <div className="d-flex flex-column align-items-center py-4 gap-3">
                        <i className="fa-brands fa-discord fs-1 text-muted mb-2"></i>
                        <span className="text-center text-muted">
                            Conecta tu cuenta de Discord para ver el estado de voz y controlar el bot de reproducción.
                        </span>
                        <button className="btn btn-discord mt-2" onClick={handleConnectDiscord}>
                            <i className="fa-brands fa-discord"></i> Conectar Discord
                        </button>
                    </div>
                ) : loadingVoice ? (
                    <div className="d-flex align-items-center justify-content-center py-4 gap-3">
                        <span className="spinner-border text-primary" role="status" style={{ width: '1.5rem', height: '1.5rem' }}></span>
                        <span>Comprobando canal de voz...</span>
                    </div>
                ) : voiceStatus ? (
                    (voiceStatus.success && voiceStatus.voiceChannelId) ? (
                        <div className="d-flex flex-column gap-3">
                            <div className="p-3 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <div className="mb-2">
                                    <strong>Servidor:</strong> <span className="text-white ms-1">{voiceStatus.guildName || 'Servidor Desconocido'}</span>
                                </div>
                                <div>
                                    <strong>Canal:</strong> <span className="text-white ms-1">{voiceStatus.voiceChannelName || 'Canal Desconocido'}</span>
                                </div>
                            </div>

                            {botConnectionState === 'conectado' && (
                                <div className="d-flex flex-column gap-3">
                                    <div className="d-flex align-items-center gap-2 text-success font-weight-bold">
                                        <i className="fa-solid fa-circle-check fs-5"></i>
                                        <span>Bot conectado a tu canal de voz</span>
                                    </div>
                                    {isBotMode ? (
                                        <button className="btn btn-danger w-100" onClick={() => handleToggleBotMode(false)}>
                                            Desactivar Modo Bot
                                        </button>
                                    ) : (
                                        <button className="btn btn-discord w-100" onClick={() => handleToggleBotMode(true)}>
                                            Activar Modo Bot
                                        </button>
                                    )}
                                </div>
                            )}

                            {botConnectionState === 'disponible' && (
                                <div className="d-flex flex-column gap-3">
                                    <div className="d-flex align-items-center gap-2 text-info font-weight-bold">
                                        <i className="fa-solid fa-circle-info fs-5"></i>
                                        <span>Bot disponible en el servidor</span>
                                    </div>
                                    {isBotMode ? (
                                        <button className="btn btn-danger w-100" onClick={() => handleToggleBotMode(false)}>
                                            Desactivar Modo Bot
                                        </button>
                                    ) : (
                                        <button className="btn btn-discord w-100" onClick={handleInviteAndControl}>
                                            Invitar y Controlar Bot
                                        </button>
                                    )}
                                </div>
                            )}

                            {botConnectionState === 'ocupado' && (
                                <div className="d-flex flex-column gap-3">
                                    <div className="d-flex align-items-center gap-2 text-warning font-weight-bold">
                                        <i className="fa-solid fa-circle-exclamation fs-5"></i>
                                        <span>Bot ocupado en otro canal</span>
                                    </div>
                                    <p className="text-muted small mb-0">
                                        El bot se encuentra reproduciendo música en otro canal de voz de este servidor.
                                    </p>
                                    {isBotMode ? (
                                        <button className="btn btn-danger w-100" onClick={() => handleToggleBotMode(false)}>
                                            Desactivar Modo Bot
                                        </button>
                                    ) : (
                                        <button className="btn btn-secondary w-100" disabled>
                                            Bot Ocupado
                                        </button>
                                    )}
                                </div>
                            )}

                            {botConnectionState === null && (
                                <div className="d-flex align-items-center gap-2 text-muted">
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    <span>Obteniendo estado del bot...</span>
                                </div>
                            )}

                            <button className="btn btn-discord w-100 mt-2" onClick={handleOpenDiscordVoiceModal}>
                                Actualizar
                            </button>
                        </div>
                    ) : (
                        <div className="d-flex flex-column align-items-center py-4 text-muted gap-2">
                            <i className="fa-solid fa-volume-xmark fs-2 text-danger mb-2"></i>
                            <span>No estás en ningún canal de voz de Discord</span>
                            <button className="btn btn-discord mt-3" onClick={handleOpenDiscordVoiceModal}>
                                Comprobar de nuevo
                            </button>
                        </div>
                    )
                ) : (
                    <div className="d-flex flex-column align-items-center py-4 text-warning gap-2">
                        <i className="fa-solid fa-circle-exclamation fs-2 mb-2"></i>
                        <span>No se pudo conectar con el bot de Discord</span>
                        <button className="btn btn-discord mt-3" onClick={handleOpenDiscordVoiceModal}>
                            Reintentar
                        </button>
                    </div>
                )}
            </div>
        </ScrollModal>
    );
});

DiscordVoiceModal.displayName = "DiscordVoiceModal";
export default DiscordVoiceModal;
